import { User, Organization } from '@kaizenplatform/intelligence-entity';
import Store from 'repatch';
import { History } from 'history';
import State from '../core/State';
import ErrorPresenter from '../service/ErrorPresenter';
import AuthUserWebApi, { MeNotFound } from '../service/persistent/AuthUserWebApi';
import { disposeUser, finishAuthentication, setSignedInUser, startAuthentication } from '../thunk/authentication';
import { startFetchingOrganizations, finishFetchingOrganizations } from '../thunk/availableOrganizations';
import OrganizationWebApiId, { PermissionAction } from '../service/persistent/OrganizationWebApiId';
import ApiErrorHandler from '../service/ApiErrorHandler';
import AuthenticationWebApi from '../service/persistent/AuthenticationWebApi';
import { User as GraghqlUser } from '../generated/graphql';
import globalState from '../operations/commands';

class AuthenticationUsecase {
  private store: Store<State>;
  private history: History;
  private authenticationWebApi: AuthenticationWebApi;
  private errorPresenter: ErrorPresenter;
  private apiErrorHandler: ApiErrorHandler;
  private authUserWebApi: AuthUserWebApi;
  private organizationWebApiId: OrganizationWebApiId;

  constructor({
    store,
    history,
    authenticationWebApi,
    errorPresenter,
    apiErrorHandler,
    authUserWebApi,
    organizationWebApiId,
  }: {
    store: Store<State>;
    history: History;
    authenticationWebApi: AuthenticationWebApi;
    errorPresenter: ErrorPresenter;
    apiErrorHandler: ApiErrorHandler;
    authUserWebApi: AuthUserWebApi;
    organizationWebApiId: OrganizationWebApiId;
  }) {
    this.store = store;
    this.history = history;
    this.authenticationWebApi = authenticationWebApi;
    this.errorPresenter = errorPresenter;
    this.apiErrorHandler = apiErrorHandler;
    this.authUserWebApi = authUserWebApi;
    this.organizationWebApiId = organizationWebApiId;
  }
  public needSignedIn = (): void => {
    const user = this.store.getState().authentication.signedInUser;

    if (!user) {
      const err = new Error('message.page_that_require_signin');
      this.errorPresenter.showAsNonFatal(err);
      throw err;
    }
  };

  public redirectKaizenIdforSignUp = (url: string): void => {
    const user = this.store.getState().authentication.signedInUser;
    user ? this.history.replace('/') : location.replace(url);
  };

  public signOut = async (): Promise<void> => {
    await this.disposeSession();
    this.disposeUser();
    this.history.push('/');
  };

  private disposeSession = async (): Promise<void> => {
    try {
      await this.authenticationWebApi.signOut();
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    }
  };

  public signInFromPreviousSession = async (): Promise<void> => {
    this.store.dispatch(startAuthentication());

    try {
      await this.identify();
    } catch (error) {
      this.authenticationFailed(error as Error);
    } finally {
      this.store.dispatch(finishAuthentication());
    }
  };

  private identify = async (): Promise<void> => {
    try {
      await this.prepareOrganizations();
      await this.prepareUser();
    } catch (error) {
      this.authenticationFailed(error as Error);
      throw error;
    }
  };

  private prepareOrganizations = async (): Promise<Organization[]> => {
    this.store.dispatch(startFetchingOrganizations());

    let organizations: Organization[];

    try {
      organizations = await this.organizationWebApiId.getAllAvailableOrganizations([
        PermissionAction.ORGANIZATIONS_READ,
        PermissionAction.ORGANIZATIONS_WRITE,
        PermissionAction.MEMBERSHIPS_READ,
        PermissionAction.MEMBERSHIPS_WRITE,
      ]);
      // organizations = await this.organizationWebApi.getStubAllAvailableOrganizations();
      // if (organizations.length === 0) {
      //   throw new OrganizationNotFound();
      // }
    } catch (error) {
      this.store.dispatch(finishFetchingOrganizations([]));
      throw error;
    }

    this.store.dispatch(finishFetchingOrganizations(organizations));

    return organizations;
  };

  private prepareUser = async (): Promise<User> => {
    let user: User;

    try {
      user = await this.authUserWebApi.getMe();
      // user = await this.authUserWebApi.getStubMe();
    } catch (err) {
      if (err instanceof MeNotFound) {
        throw new UserNotFound();
      }
      throw err;
    }

    this.store.dispatch(setSignedInUser(user));

    // -- tmp bug workaround for Root.tsx --
    const { email, name, profileImageUrl, id } = user;
    const __future__replace__user: GraghqlUser = {
      email: email,
      name: name ?? '',
      profileImageUrl: profileImageUrl ?? '',
      id: id,
    };
    globalState.setCurrentUser(__future__replace__user);
    // -- tmp bug workaround for Root.tsx --

    return user;
  };

  private disposeUser = () => {
    this.store.dispatch(disposeUser());
  };

  private authenticationFailed = (err: Error, message?: string): void => {
    if (err instanceof OrganizationNotFound) {
      message = 'No available organizations';
    }

    if (!message) {
      message = 'Authentication has failed';
    }

    this.errorPresenter.showAsNonFatal(new Error(message));
  };
}

class OrganizationNotFound extends Error {
  public readonly name = 'OrganizationNotFound';
  public readonly message = 'No available organization is found.';

  constructor() {
    super();
  }
}

class UserNotFound extends Error {
  public readonly name = 'UserNotFound';
  public readonly message = 'No available User is found.';

  constructor() {
    super();
  }
}

export default AuthenticationUsecase;
